import {
	DealstackAiEntityListTab,
	DealstackAiTransactionQuickFilter,
} from '@dealstack/shared/lib/backendClient';
import type { BatchListMode, SearchParamPatch, WorkspaceUrlState } from './workspaceUrlState.types';

/** Shared search-param keys. Categories add their own keys in their folder, prefixed by category. */
export const WORKSPACE_PARAMS = {
	issuer: 'issuer',
	overview: 'overview',
	panel: 'panel',
	batchList: 'batchList',
	selected: 'selected',
	tab: 'tab',
	resolve: 'resolve',
	resolveTab: 'resolveTab',
	expandedTransaction: 'tx',
	quickFilter: 'txFilter',
	quickFiltersMenu: 'quickFilters',
	viewer: 'viewer',
	viewerField: 'viewerField',
	viewerRow: 'viewerRow',
	viewerPage: 'viewerPage',
	spotlight: 'spotlight',
	/** Open right-rail panels, oldest first: `details,source`. */
	dock: 'dock',
} as const;

export const COLLAPSED = 'collapsed';
export const OPEN = 'open';

/**
 * The `overview` param after picking a category: folded at and below the workspace's 1599px fold-overview step, so the
 * category's panels have room, and left as it was above that.
 */
export const overviewOnCategoryPick = (current: string | null): string | null =>
	window.matchMedia('(max-width: 1599px)').matches ? COLLAPSED : current;

/** Params that survive switching category: the layout state. */
const STICKY_PARAMS: readonly string[] = [
	WORKSPACE_PARAMS.issuer,
	WORKSPACE_PARAMS.overview,
	WORKSPACE_PARAMS.panel,
	WORKSPACE_PARAMS.batchList,
	WORKSPACE_PARAMS.dock,
];

/** Middle-panel tab order, shared by every category. */
const ENTITY_LIST_TABS: readonly DealstackAiEntityListTab[] =
	Object.values(DealstackAiEntityListTab);

const QUICK_FILTERS: readonly DealstackAiTransactionQuickFilter[] = Object.values(
	DealstackAiTransactionQuickFilter
);

export const isOneOf = <T extends string>(
	value: string | null,
	options: readonly T[]
): value is T => options.some((option) => option === value);

/** `value` when it is one of `options`, otherwise `fallback`. */
export const oneOfOr = <T extends string>(
	value: string | null,
	options: readonly T[],
	fallback: T
): T => (isOneOf(value, options) ? value : fallback);

/** Search-param value that drops out of the URL when it is the default. */
export const unlessDefault = <T extends string>(value: T, defaultValue: T): T | null =>
	value === defaultValue ? null : value;

const toBatchListMode = (value: string | null): BatchListMode =>
	value === 'popover' || value === 'docked' ? value : 'collapsed';

export const parseWorkspaceUrlState = (params: URLSearchParams): WorkspaceUrlState => ({
	issuerId: params.get(WORKSPACE_PARAMS.issuer),
	overviewCollapsed: params.get(WORKSPACE_PARAMS.overview) === COLLAPSED,
	panelCollapsed: params.get(WORKSPACE_PARAMS.panel) === COLLAPSED,
	batchList: toBatchListMode(params.get(WORKSPACE_PARAMS.batchList)),
	selectedId: params.get(WORKSPACE_PARAMS.selected),
	tab: oneOfOr(params.get(WORKSPACE_PARAMS.tab), ENTITY_LIST_TABS, 'all'),
	resolveId: params.get(WORKSPACE_PARAMS.resolve),
	resolveTab: params.get(WORKSPACE_PARAMS.resolveTab),
	expandedTransactionId: params.get(WORKSPACE_PARAMS.expandedTransaction),
	quickFilter: oneOfOr(params.get(WORKSPACE_PARAMS.quickFilter), QUICK_FILTERS, 'all'),
	quickFiltersOpen: params.get(WORKSPACE_PARAMS.quickFiltersMenu) === OPEN,
});

export const applySearchParamPatch = (
	params: URLSearchParams,
	patch: SearchParamPatch
): URLSearchParams => {
	const next = new URLSearchParams(params);
	Object.entries(patch).forEach(([key, value]) => {
		if (value === null) {
			next.delete(key);
		} else {
			next.set(key, value);
		}
	});
	return next;
};

/** Search string for another workspace view: keeps sticky params, adds `extra`. */
export const carryOverSearch = (params: URLSearchParams, extra: SearchParamPatch = {}): string => {
	const kept = new URLSearchParams();
	STICKY_PARAMS.forEach((key) => {
		const value = params.get(key);
		if (value !== null) {
			kept.set(key, value);
		}
	});
	const next = applySearchParamPatch(kept, extra).toString();
	return next ? `?${next}` : '';
};

/** Switching issuer clears every drill-down/filter; only the layout survives. */
export const issuerScopePatch = (
	params: URLSearchParams,
	issuerId: string | null
): SearchParamPatch => {
	const patch: SearchParamPatch = { [WORKSPACE_PARAMS.issuer]: issuerId };
	for (const key of params.keys()) {
		if (!STICKY_PARAMS.includes(key)) {
			patch[key] = null;
		}
	}
	return patch;
};
